import { createContext, useEffect, useReducer } from "react";
import type { Dispatch } from "react";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import { useReadAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type {
  AccountConfigurationPayload,
  AccountTreeNode,
  FilterDataPayload,
  GroupsPayload,
  HttpError,
  IncentiveContentDefaultsPayload,
  StateDisclaimersPayload
} from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { isHttpError } from "@dealer-center/ddc-pages-incentives-manager-interface";

export interface PreferencesProviderContextInterface {
  accountConfiguration: AccountConfigurationPayload | null;
  contentDefaults: IncentiveContentDefaultsPayload | null;
  stateDisclaimer: StateDisclaimersPayload | null;
  groups: GroupsPayload | null;
  accountTree: AccountTreeNode | null;
  filterData: FilterDataPayload | null;
  isLoading: boolean;
  error: boolean;
}

export const defaultPreferences: PreferencesProviderContextInterface = {
  accountConfiguration: null,
  contentDefaults: null,
  stateDisclaimer: null,
  groups: null,
  accountTree: null,
  filterData: null,
  isLoading: true,
  error: false
};

interface PreferencesProviderProps {
  children: React.ReactElement | React.ReactElement[];
  userInfo: ControlcenterAPIInterfaceExternal;
}

export function reducer(state, action) {
  switch (action.type) {
    case "SET_ERROR":
      return {
        ...state,
        error: action.error,
        isLoading: false
      };
    case "SET_ACCOUNT_CONFIGURATION":
      return {
        ...state,
        accountConfiguration: action.accountConfigurationData,
        isLoading:
          !state.error &&
          !state.contentDefaults &&
          !state.stateDisclaimer &&
          !state.groups &&
          !state.accountTree &&
          !state.filterDat
      };
    case "SET_CONTENT_DEFAULTS":
      return {
        ...state,
        contentDefaults: action.contentDefaultsData,
        isLoading:
          !state.error &&
          !state.accountConfiguration &&
          !state.stateDisclaimer &&
          !state.groups &&
          !state.accountTree &&
          !state.filterData
      };
    case "SET_STATE_DISCLAIMERS":
      return {
        ...state,
        stateDisclaimer: action.stateDisclaimersData,
        isLoading:
          !state.error &&
          !state.accountConfiguration &&
          !state.contentDefaults &&
          !state.groups &&
          !state.accountTree &&
          !state.filterData
      };
    case "SET_FILTER_DATA":
      return {
        ...state,
        filterData: action.filterData,
        isLoading:
          !state.error &&
          !state.accountConfiguration &&
          !state.contentDefaults &&
          !state.stateDisclaimer &&
          !state.groups &&
          !state.accountTree
      };
    case "SET_GROUPS":
      return {
        ...state,
        groups: action.groups,
        isLoading:
          !state.error &&
          !state.accountConfiguration &&
          !state.contentDefaults &&
          !state.stateDisclaimer &&
          !state.accountTree &&
          !state.filterData
      };
    case "SET_ACCOUNT_TREE":
      return {
        ...state,
        accountTree: action.accountTree,
        isLoading:
          !state.error &&
          !state.accountConfiguration &&
          !state.contentDefaults &&
          !state.stateDisclaimer &&
          !state.groups &&
          !state.filterData
      };
    default:
      break;
  }
  return state;
}

const isMock = new URLSearchParams(window.location.search).has("mock");

interface PreferencesContextType {
  preferences: PreferencesProviderContextInterface;
  dispatchPreferences: Dispatch<any>;
}

export const PreferencesContext = createContext<PreferencesContextType>({
  preferences: defaultPreferences,
  dispatchPreferences: (() => undefined) as Dispatch<any>
});

export const PreferencesProvider = ({
  children,
  userInfo
}: PreferencesProviderProps) => {
  const [preferences, dispatchPreferences] = useReducer(
    reducer,
    defaultPreferences
  );

  const {
    isLoading: isAccountConfigurationLoading,
    isError: isAccountConfigurationError,
    data: accountConfigurationData
  } = useReadAPI({
    resource: "account-configuration",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isContentDefaultsLoading,
    isError: isContentDefaultsError,
    data: contentDefaultsData
  } = useReadAPI({
    resource: "incentive-content-defaults",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isStateDisclaimersLoading,
    isError: isStateDisclaimersError,
    data: stateDisclaimersData
  } = useReadAPI({
    resource: "state-disclaimers",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isDistributionGroupsLoading,
    isError: isDistributionGroupsError,
    data: distributionGroupsData
  } = useReadAPI({
    resource: "distribution-lists",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isDistributionFilterDataLoading,
    isError: isDistributionFilterDataError,
    data: distributionFilterData
  } = useReadAPI({
    resource: "distribution-lists-filter-data",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isDistributionAccountTreeLoading,
    isError: isAccountTreeError,
    data: accountTree
  } = useReadAPI({
    resource: "distribution-lists-account-tree",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  useEffect(() => {
    if (
      accountConfigurationData &&
      !isAccountConfigurationError &&
      !isAccountConfigurationLoading
    ) {
      if (isHttpError(accountConfigurationData)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: accountConfigurationData as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_ACCOUNT_CONFIGURATION",
          accountConfigurationData
        });
      }
    }
  }, [
    isAccountConfigurationLoading,
    accountConfigurationData,
    isAccountConfigurationError
  ]);

  useEffect(() => {
    if (
      contentDefaultsData &&
      !isContentDefaultsError &&
      !isContentDefaultsLoading
    ) {
      if (isHttpError(contentDefaultsData)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: contentDefaultsData as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_CONTENT_DEFAULTS",
          contentDefaultsData
        });
      }
    }
  }, [contentDefaultsData, isContentDefaultsError, isContentDefaultsLoading]);

  useEffect(() => {
    if (
      stateDisclaimersData &&
      !isStateDisclaimersError &&
      !isStateDisclaimersLoading
    ) {
      if (isHttpError(stateDisclaimersData)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: stateDisclaimersData as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_STATE_DISCLAIMERS",
          stateDisclaimersData
        });
      }
    }
  }, [
    stateDisclaimersData,
    isStateDisclaimersError,
    isStateDisclaimersLoading
  ]);

  useEffect(() => {
    if (
      distributionGroupsData &&
      !isDistributionGroupsError &&
      !isDistributionGroupsLoading
    ) {
      if (isHttpError(distributionGroupsData)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: distributionGroupsData as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_GROUPS",
          groups: distributionGroupsData
        });
      }
    }
  }, [
    distributionGroupsData,
    isDistributionGroupsError,
    isDistributionGroupsLoading
  ]);

  useEffect(() => {
    if (
      distributionFilterData &&
      !isDistributionFilterDataError &&
      !isDistributionFilterDataLoading
    ) {
      if (isHttpError(distributionFilterData)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: distributionFilterData as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_FILTER_DATA",
          filterData: distributionFilterData
        });
      }
    }
  }, [
    distributionFilterData,
    isDistributionFilterDataError,
    isDistributionFilterDataLoading
  ]);

  //
  useEffect(() => {
    if (
      accountTree &&
      !isAccountTreeError &&
      !isDistributionAccountTreeLoading
    ) {
      if (isHttpError(accountTree)) {
        dispatchPreferences({
          type: "SET_ERROR",
          error: accountTree as HttpError
        });
      } else {
        dispatchPreferences({
          type: "SET_ACCOUNT_TREE",
          accountTree
        });
      }
    }
  }, [accountTree, isAccountTreeError, isDistributionAccountTreeLoading]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        dispatchPreferences
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
